import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { dailyApi, getToken } from "../lib/api";
import { toast } from "../hooks/use-toast";

let state = { notifications: [], unread: 0 };
const subs = new Set();
const SEEN_KEY = "fc_seen_notifs";
const seen = new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"));

const refresh = async () => {
  if (!getToken()) return;
  try {
    state = await dailyApi.notifications();
    const fresh = state.notifications.filter((n) => !n.read && !seen.has(n.id));
    fresh.slice(0, 3).forEach((n) => toast({ title: n.title, description: n.body }));
    fresh.forEach((n) => seen.add(n.id));
    if (fresh.length) localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-100)));
    subs.forEach((f) => f(state));
  } catch (e) { /* offline */ }
};

// Mounted once in App: polls for new alerts and toasts them on whatever page is open.
export const NotificationsPoller = () => {
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);
  return null;
};

const ago = (ts) => {
  const m = Math.max(1, Math.round((Date.now() / 1000 - ts) / 60));
  return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`;
};

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(state);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    subs.add(setData);
    return () => subs.delete(setData);
  }, []);

  const onOpen = (o) => {
    setOpen(o);
    if (o && data.unread) {
      dailyApi.readNotifications().catch(() => {});
      state = { ...state, unread: 0, notifications: state.notifications.map((n) => ({ ...n, read: true })) };
      setTimeout(() => subs.forEach((f) => f(state)), 1500);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell-btn" className="relative rounded-xl bg-white/5 border border-white/10 p-2 hover:border-[#10B981]/50 transition-colors shrink-0" title="Alerts">
          <Bell className="w-4 h-4 text-slate-200" />
          {data.unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center px-1">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="theme-light w-72 bg-[#1E293B] opacity-100 shadow-2xl z-[60] border border-white/10 text-white p-2" data-testid="notif-panel">
        <div className="px-2 py-1.5 text-xs font-semibold label-eyebrow text-slate-400">Alerts</div>
        {data.notifications.length === 0 ? (
          <div className="px-2 py-4 text-sm text-slate-400" data-testid="notif-empty">Nothing yet. Challenge a mate!</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => { setOpen(false); navigate(n.link); }}
                className={`w-full text-left rounded-lg px-2 py-2 hover:bg-white/5 transition-colors ${n.read ? "" : "bg-[#10B981]/[0.07]"}`}>
                <div className="text-sm font-semibold leading-snug">{n.title}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{ago(n.at)}</div>
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
